/* Wiltschut Commerce Mission Control: track-your-order widget.
 * Vanilla JS, no dependencies. Talks to the public track API (POST for
 * email/zip lookups so no PII lands in URLs; GET only for signed deep
 * links). All DOM nodes are built with textContent, never innerHTML
 * with response data. Installed as assets/ni-track.js by
 * lib/shopify/track-page.ts.
 *
 * Design: the signature element is the journey bar (Ordered > Shipped >
 * In transit > Delivered) with a track that fills to the parcel's
 * current position. Typography and color stay theme-neutral so the
 * widget reads as native inside every store theme. */
(function () {
  "use strict";

  var root = document.getElementById("ni-track");
  if (!root) return;

  var API = root.getAttribute("data-api") || "";
  var STORE = root.getAttribute("data-store") || "";
  var LOCALE = (root.getAttribute("data-locale") || "en").toLowerCase();
  var CONTACT = root.getAttribute("data-contact") || "";
  // Shopify hands us the request locale ("fr", "fr-FR", "de-DE"). Match
  // on the language subtag and fall back to English for anything we do
  // not translate.
  var LANG =
    LOCALE.indexOf("de") === 0 ? "de" : LOCALE.indexOf("fr") === 0 ? "fr" : "en";
  // The locale to format dates, times and country names in.
  var INTL_LOCALE =
    LANG === "de" ? "de-DE" : LANG === "fr" ? "fr-FR" : "en-US";

  var STRINGS = {
    en: {
      orderLabel: "Order number",
      orderPlaceholder: "e.g. #1234",
      emailLabel: "Email address",
      zipLabel: "Postal code",
      useZip: "Use postal code instead",
      useEmail: "Use email address instead",
      submit: "Track my order",
      formHint: "Enter the order number from your confirmation email.",
      loading: "Looking up your order...",
      notFound:
        "We could not find that order. Check the order number and try again.",
      genericError: "Something went wrong. Please try again in a minute.",
      orderEyebrow: "Order",
      placed: "Placed",
      shipmentOf: function (i, n) {
        return "Shipment " + i + " of " + n;
      },
      steps: ["Ordered", "Packed", "On its way", "In your country", "Out for delivery", "Delivered"],
      stepTitle: {
        ordered: "Order received",
        packed: "Packed and ready to ship",
        on_its_way: "On its way to you",
        in_country: "In your country",
        out_for_delivery: "Out for delivery",
        delivered: "Delivered",
      },
      stepText: {
        ordered: "We have your order and are getting it ready to ship.",
        packed:
          "Your order is packed and labeled. The carrier picks it up next, and tracking starts with its first scan.",
        on_its_way:
          "Your parcel left our warehouse and is traveling to your country. This is the longest part of the trip, and it can go a few days without new scans. That is normal.",
        in_country:
          "Your parcel has arrived in your country and is moving through the local postal network toward your address.",
        out_for_delivery:
          "Your parcel is with the local carrier for delivery and should arrive today.",
        delivered: "Your parcel was delivered. Enjoy your order!",
      },
      actionHeadline: {
        pickup: "Ready for pickup",
        missed_delivery: "Delivery attempt missed",
        contact_us: "Delivery on hold",
      },
      actionSub: {
        pickup: "Your parcel is waiting for you to collect it.",
        missed_delivery: "The carrier came by but could not hand it over.",
        contact_us: "The delivery is paused until a small issue is solved.",
      },
      actionTitle: "Action needed",
      actionText: {
        pickup:
          "Your parcel is waiting at your local post office or pickup point. The address is on the notice from your carrier. Bring a photo ID.",
        missed_delivery:
          "The carrier tried to deliver but could not. Use the notice in your mailbox to rebook or pick it up. Need a hand? Email us.",
        contact_us:
          "Something is holding up the delivery. Email us with your order number and we will sort it out with the carrier.",
      },
      noActionTitle: "Nothing you need to do",
      noActionBody:
        "Your parcel is moving as planned. This page updates by itself with every scan.",
      deliveredHelpTitle: "Can't find it?",
      deliveredHelp:
        "Check your mailbox, porch and with neighbors. Still missing after a day? Email us.",
      now: "Now",
      expectedTitle: "Expected delivery",
      historyToggle: function (n) {
        return "See all carrier scans (" + n + ")";
      },
      lateTitle: "Taking a little longer than expected",
      lateBody:
        "Your parcel has passed its usual delivery window. Most delayed parcels still arrive within a few days. Want us to look into it? Email us and we will check with the carrier.",
      quietNote: function (n) {
        return (
          "No new scans for " +
          n +
          " days. International legs can be quiet for a few days at a time."
        );
      },
      contactTitle: "Questions about your order?",
      contactCta: "Email us",
      mailSubject: function (order) {
        return "Question about order " + order;
      },
      mailSubjectGeneric: "Question about my order",
    },
    de: {
      orderLabel: "Bestellnummer",
      orderPlaceholder: "z. B. #1234",
      emailLabel: "E-Mail-Adresse",
      zipLabel: "Postleitzahl",
      useZip: "Stattdessen Postleitzahl verwenden",
      useEmail: "Stattdessen E-Mail-Adresse verwenden",
      submit: "Bestellung verfolgen",
      formHint: "Die Bestellnummer steht in Ihrer Bestellbestaetigung.",
      loading: "Bestellung wird gesucht...",
      notFound:
        "Wir konnten diese Bestellung nicht finden. Bitte Bestellnummer pruefen und erneut versuchen.",
      genericError:
        "Etwas ist schiefgelaufen. Bitte in einer Minute erneut versuchen.",
      orderEyebrow: "Bestellung",
      placed: "Bestellt am",
      shipmentOf: function (i, n) {
        return "Sendung " + i + " von " + n;
      },
      steps: ["Bestellt", "Verpackt", "Unterwegs", "In Ihrem Land", "In Zustellung", "Zugestellt"],
      stepTitle: {
        ordered: "Bestellung erhalten",
        packed: "Verpackt und versandbereit",
        on_its_way: "Auf dem Weg zu Ihnen",
        in_country: "In Ihrem Land",
        out_for_delivery: "In Zustellung",
        delivered: "Zugestellt",
      },
      stepText: {
        ordered: "Wir haben Ihre Bestellung und bereiten den Versand vor.",
        packed:
          "Ihre Bestellung ist verpackt und beschriftet. Als Naechstes holt der Versanddienst sie ab, mit dem ersten Scan beginnt das Tracking.",
        on_its_way:
          "Ihr Paket hat unser Lager verlassen und ist auf dem Weg in Ihr Land. Das ist der laengste Teil der Reise, und es kann ein paar Tage ohne neue Scans vergehen. Das ist normal.",
        in_country:
          "Ihr Paket ist in Ihrem Land angekommen und auf dem Weg zu Ihrer Adresse.",
        out_for_delivery:
          "Ihr Paket ist beim Zusteller und kommt voraussichtlich heute an.",
        delivered: "Ihr Paket wurde zugestellt. Viel Freude damit!",
      },
      actionHeadline: {
        pickup: "Abholbereit",
        missed_delivery: "Zustellversuch verpasst",
        contact_us: "Zustellung angehalten",
      },
      actionSub: {
        pickup: "Ihr Paket wartet darauf, abgeholt zu werden.",
        missed_delivery: "Der Zusteller war da, konnte das Paket aber nicht abgeben.",
        contact_us: "Die Zustellung ruht, bis eine Kleinigkeit geklaert ist.",
      },
      actionTitle: "Bitte tun Sie Folgendes",
      actionText: {
        pickup:
          "Ihr Paket liegt in Ihrer Postfiliale oder an einer Abholstelle. Die Adresse steht auf der Benachrichtigung des Zustellers. Bitte einen Ausweis mitnehmen.",
        missed_delivery:
          "Der Zusteller konnte das Paket nicht abgeben. Mit der Benachrichtigung in Ihrem Briefkasten koennen Sie eine neue Zustellung waehlen oder es abholen. Hilfe noetig? Schreiben Sie uns.",
        contact_us:
          "Etwas haelt die Zustellung auf. Schreiben Sie uns mit Ihrer Bestellnummer, wir klaeren es mit dem Versanddienst.",
      },
      noActionTitle: "Sie muessen nichts tun",
      noActionBody:
        "Ihr Paket ist planmaessig unterwegs. Diese Seite aktualisiert sich bei jedem Scan.",
      deliveredHelpTitle: "Nicht gefunden?",
      deliveredHelp:
        "Schauen Sie im Briefkasten, vor der Tuer und bei Nachbarn. Nach einem Tag immer noch nichts? Schreiben Sie uns.",
      now: "Jetzt",
      expectedTitle: "Voraussichtliche Zustellung",
      historyToggle: function (n) {
        return "Alle Scans des Versanddienstes anzeigen (" + n + ")";
      },
      lateTitle: "Dauert etwas laenger als erwartet",
      lateBody:
        "Ihre Sendung hat das uebliche Zustellfenster ueberschritten. Die meisten verspaeteten Pakete kommen dennoch innerhalb weniger Tage an. Schreiben Sie uns eine E-Mail, dann fragen wir beim Versanddienst nach.",
      quietNote: function (n) {
        return (
          "Seit " +
          n +
          " Tagen keine neuen Scans. Internationale Etappen koennen einige Tage still sein."
        );
      },
      contactTitle: "Fragen zu Ihrer Bestellung?",
      contactCta: "E-Mail schreiben",
      mailSubject: function (order) {
        return "Frage zu Bestellung " + order;
      },
      mailSubjectGeneric: "Frage zu meiner Bestellung",
    },
    fr: {
      orderLabel: "Numéro de commande",
      orderPlaceholder: "ex. #1234",
      emailLabel: "Adresse e-mail",
      zipLabel: "Code postal",
      useZip: "Utiliser le code postal",
      useEmail: "Utiliser l'adresse e-mail",
      submit: "Suivre ma commande",
      formHint:
        "Saisissez le numéro de commande figurant dans votre e-mail de confirmation.",
      loading: "Recherche de votre commande...",
      notFound:
        "Nous n'avons pas trouvé cette commande. Vérifiez le numéro et réessayez.",
      genericError:
        "Une erreur est survenue. Merci de réessayer dans une minute.",
      orderEyebrow: "Commande",
      placed: "Commandée le",
      shipmentOf: function (i, n) {
        return "Colis " + i + " sur " + n;
      },
      steps: ["Commandée", "Préparée", "En route", "Dans votre pays", "En livraison", "Livrée"],
      stepTitle: {
        ordered: "Commande reçue",
        packed: "Préparée et prête à partir",
        on_its_way: "En route vers vous",
        in_country: "Dans votre pays",
        out_for_delivery: "En cours de livraison",
        delivered: "Livrée",
      },
      stepText: {
        ordered: "Nous avons bien reçu votre commande et la préparons pour l'envoi.",
        packed:
          "Votre commande est emballée et étiquetée. Le transporteur vient la récupérer, et le suivi démarre à son premier scan.",
        on_its_way:
          "Votre colis a quitté notre entrepôt et voyage vers votre pays. C'est la plus longue partie du trajet, et il peut rester quelques jours sans nouveau scan. C'est normal.",
        in_country:
          "Votre colis est arrivé dans votre pays et poursuit sa route vers votre adresse.",
        out_for_delivery:
          "Votre colis est chez le livreur et devrait arriver aujourd'hui.",
        delivered: "Votre colis a été livré. Profitez bien de votre commande !",
      },
      actionHeadline: {
        pickup: "Prêt pour le retrait",
        missed_delivery: "Livraison manquée",
        contact_us: "Livraison en attente",
      },
      actionSub: {
        pickup: "Votre colis attend que vous veniez le chercher.",
        missed_delivery: "Le livreur est passé mais n'a pas pu remettre le colis.",
        contact_us: "La livraison est en pause le temps de régler un petit souci.",
      },
      actionTitle: "Action requise",
      actionText: {
        pickup:
          "Votre colis vous attend au bureau de poste ou au point relais. L'adresse figure sur l'avis du transporteur. Pensez à une pièce d'identité.",
        missed_delivery:
          "Le transporteur n'a pas pu livrer. Suivez l'avis laissé dans votre boîte aux lettres pour reprogrammer ou retirer le colis. Besoin d'aide ? Écrivez-nous.",
        contact_us:
          "Quelque chose bloque la livraison. Écrivez-nous avec votre numéro de commande et nous réglons cela avec le transporteur.",
      },
      noActionTitle: "Vous n'avez rien à faire",
      noActionBody:
        "Votre colis avance comme prévu. Cette page se met à jour à chaque scan.",
      deliveredHelpTitle: "Vous ne le trouvez pas ?",
      deliveredHelp:
        "Vérifiez votre boîte aux lettres, devant la porte et auprès des voisins. Toujours rien après un jour ? Écrivez-nous.",
      now: "Maintenant",
      expectedTitle: "Livraison prévue",
      historyToggle: function (n) {
        return "Voir tous les scans du transporteur (" + n + ")";
      },
      lateTitle: "Un peu plus long que prévu",
      lateBody:
        "Votre colis a dépassé son délai de livraison habituel. La plupart des colis en retard arrivent malgré tout sous quelques jours. Vous voulez que nous vérifiions ? Écrivez-nous et nous contacterons le transporteur.",
      quietNote: function (n) {
        return (
          "Aucun nouveau scan depuis " +
          n +
          " jours. Les trajets internationaux peuvent rester silencieux plusieurs jours."
        );
      },
      contactTitle: "Une question sur votre commande ?",
      contactCta: "Écrivez-nous",
      mailSubject: function (order) {
        return "Question concernant la commande " + order;
      },
      mailSubjectGeneric: "Question concernant ma commande",
    },
  };
  var T = STRINGS[LANG];

  // The six customer steps, in order. The API sends shipment.step (one
  // of these keys, decided server-side in lib/parcel-sync/customer-step.ts)
  // and shipment.action; STEP_BY_STATUS is only the fallback for an API
  // response from before those fields existed.
  var STEP_KEYS = [
    "ordered",
    "packed",
    "on_its_way",
    "in_country",
    "out_for_delivery",
    "delivered",
  ];
  var STEP_BY_STATUS = {
    PENDING: "packed",
    INFO_RECEIVED: "packed",
    OUT_FOR_DELIVERY: "out_for_delivery",
    READY_FOR_PICKUP: "out_for_delivery",
    FAILED_ATTEMPT: "out_for_delivery",
    DELIVERED: "delivered",
  };
  var ACTION_BY_STATUS = {
    READY_FOR_PICKUP: "pickup",
    FAILED_ATTEMPT: "missed_delivery",
    EXCEPTION: "contact_us",
  };

  function stepOf(shipment) {
    var key = shipment.step || STEP_BY_STATUS[shipment.status] || "on_its_way";
    var index = STEP_KEYS.indexOf(key);
    return index < 0 ? 2 : index;
  }

  function actionOf(shipment) {
    return shipment.action || ACTION_BY_STATUS[shipment.status] || "none";
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function clear() {
    while (root.firstChild) root.removeChild(root.firstChild);
  }

  function formatDate(iso) {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(
        INTL_LOCALE,
        { year: "numeric", month: "long", day: "numeric" },
      );
    } catch {
      return "";
    }
  }

  // Static inline icons (no user data ever flows through innerHTML).
  var ICONS = {
    ordered:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="5.5" y="8" width="13" height="11.5" rx="2"/><path d="M9 8.5V7a3 3 0 0 1 6 0v1.5"/></svg>',
    shipped:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 8.2 12 4.5l7.5 3.7v7.6L12 19.5l-7.5-3.7V8.2z"/><path d="M4.5 8.2 12 12l7.5-3.8"/><path d="M12 12v7.5"/></svg>',
    transit:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 7.5h10.5v8H2.5z"/><path d="M13 10.5h3.8l2.7 2.7v2.3H13"/><circle cx="6.5" cy="17.5" r="1.7"/><circle cx="16" cy="17.5" r="1.7"/></svg>',
    delivered:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10.5 12 3l9 7.5"/><path d="M5.5 9.5V21h13V9.5"/><path d="M9.5 21v-6h5v6"/></svg>',
    plane:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M10.5 13.5 3 11l1.5-1.5 7.5 1 4.5-4.5a2.1 2.1 0 0 1 3 3L15 13.5l1 7.5-1.5 1.5-2.5-7.5-3 3V20l-1.5 1.5-1-3-3-1L5 16h2l3-3"/></svg>',
    pin:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 21s-6.5-5.6-6.5-10a6.5 6.5 0 0 1 13 0c0 4.4-6.5 10-6.5 10z"/><circle cx="12" cy="10.6" r="2.3"/></svg>',
    mail:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3.5" y="5.5" width="17" height="13" rx="2"/><path d="m4.5 7.5 7.5 5.5 7.5-5.5"/></svg>',
    check:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5.5 12.5l4.3 4.3L18.5 7"/></svg>',
    warn:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 6.5v7"/><path d="M12 17.2v.1"/></svg>',
  };
  var STAGE_ICONS = ["ordered", "shipped", "plane", "pin", "transit", "delivered"];

  function iconEl(name, className) {
    var span = el("span", className);
    span.innerHTML = ICONS[name] || "";
    return span;
  }

  function formatShortDate(iso) {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(
        INTL_LOCALE,
        { month: "short", day: "numeric" },
      );
    } catch {
      return null;
    }
  }

  // Latest visible (already origin-scrubbed) location; the CN leg never
  // reaches the browser.
  function currentLocation(shipment) {
    var cps = shipment.checkpoints || [];
    for (var i = 0; i < cps.length; i++) {
      if (cps[i] && cps[i].location) return titleCasePlace(cps[i].location);
    }
    return null;
  }

  function formatDayHeader(iso) {
    if (!iso) return "";
    try {
      var d = new Date(iso);
      var now = new Date();
      function ymd(x) {
        return x.getFullYear() + "-" + x.getMonth() + "-" + x.getDate();
      }
      if (ymd(d) === ymd(now)) return T.today;
      var y = new Date(now.getTime() - 86400000);
      if (ymd(d) === ymd(y)) return T.yesterday;
      return d.toLocaleDateString(INTL_LOCALE, {
        weekday: "long",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "";
    }
  }

  function formatTimeOnly(iso) {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleTimeString(
        INTL_LOCALE,
        { hour: "2-digit", minute: "2-digit" },
      );
    } catch {
      return "";
    }
  }

  function dayKey(iso) {
    if (!iso) return "unknown";
    try {
      var d = new Date(iso);
      return d.getFullYear() + "-" + d.getMonth() + "-" + d.getDate();
    } catch {
      return "unknown";
    }
  }

  function formatDateRange(fromIso, toIso) {
    var a = formatShortDate(fromIso);
    var b = formatShortDate(toIso);
    if (a && b) return a === b ? a : a + " \u2013 " + b;
    return a || b || "";
  }

  // Carrier feeds sometimes SHOUT the whole status. Sentence-case a
  // fully-uppercase description; leave mixed-case strings untouched.
  var DESC_ACRONYMS = ["USPS", "USA", "US", "UK", "EU", "DHL", "UPS"];
  function normalizeDescription(raw) {
    if (!raw) return raw;
    if (/[a-z]/.test(raw)) return raw;
    var lower = raw.toLowerCase();
    var sentence = lower.charAt(0).toUpperCase() + lower.slice(1);
    return sentence.replace(/\b[a-z]+\b/g, function (w) {
      return DESC_ACRONYMS.indexOf(w.toUpperCase()) > -1 ? w.toUpperCase() : w;
    });
  }

  function titleCasePlace(raw) {
    if (!raw) return "";
    // Carrier feeds shout locations ("SAN RAMON CA"); calm them down.
    return raw.replace(/\w[^\s,]*/g, function (word) {
      if (word.length <= 2) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
  }

  // ------------------------------------------------------------------
  // Form
  // ------------------------------------------------------------------

  function renderForm(prefillOrder, message) {
    clear();

    var card = el("div", "ni-card ni-form-card");
    if (message) card.appendChild(el("p", "ni-error", message));

    var form = el("form", "ni-form");

    var orderField = el("div", "ni-field");
    orderField.appendChild(el("label", "ni-label", T.orderLabel));
    var orderInput = el("input", "ni-input");
    orderInput.type = "text";
    orderInput.name = "order";
    orderInput.placeholder = T.orderPlaceholder;
    orderInput.required = true;
    orderInput.autocomplete = "off";
    if (prefillOrder) orderInput.value = prefillOrder;
    orderField.appendChild(orderInput);
    form.appendChild(orderField);

    var submit = el("button", "ni-submit", T.submit);
    submit.type = "submit";
    form.appendChild(submit);

    form.addEventListener("submit", function (evt) {
      evt.preventDefault();
      lookup({ store: STORE, order: orderInput.value }, orderInput.value);
    });

    card.appendChild(form);
    card.appendChild(el("p", "ni-hint", T.formHint));
    if (CONTACT) {
      var contactLink = el("a", "ni-form-contact", T.contactTitle);
      contactLink.href = contactHref(T.mailSubjectGeneric);
      card.appendChild(contactLink);
    }
    root.appendChild(card);
  }

  function renderLoading() {
    clear();
    var card = el("div", "ni-card ni-loading-card");
    card.appendChild(el("span", "ni-spinner"));
    card.appendChild(el("p", "ni-hint", T.loading));
    root.appendChild(card);
  }

  // ------------------------------------------------------------------
  // Result
  // ------------------------------------------------------------------

  var currentOrderNumber = "";

  function renderResult(data) {
    clear();
    currentOrderNumber = data.order.number || "";
    var wrap = el("div", "ni-result");

    var head = el("header", "ni-order-head");
    var headLeft = el("div", "ni-order-head-left");
    headLeft.appendChild(el("p", "ni-eyebrow", T.orderEyebrow));
    headLeft.appendChild(el("h2", "ni-order-number", data.order.number));
    head.appendChild(headLeft);
    if (data.order.placedAt) {
      var headRight = el("div", "ni-order-head-right");
      headRight.appendChild(el("p", "ni-eyebrow", T.placed));
      headRight.appendChild(
        el("p", "ni-order-date", formatDate(data.order.placedAt)),
      );
      head.appendChild(headRight);
    }
    wrap.appendChild(head);

    var shipments = data.shipments || [];
    for (var i = 0; i < shipments.length; i++) {
      wrap.appendChild(
        renderShipment(shipments[i], i, shipments.length, data.order),
      );
    }
    if (shipments.length === 0) {
      // Not shipped yet: still show where the order stands.
      wrap.appendChild(
        renderShipment(
          { step: "ordered", action: "none", status: "PENDING", checkpoints: [], items: [] },
          0,
          1,
          data.order,
        ),
      );
    }

    var contact = renderContact(data.order.number);
    if (contact) wrap.appendChild(contact);

    root.appendChild(wrap);
  }

  // The step bar: six fixed steps, filled up to where the parcel is now.
  function renderJourney(shipment, order, current, action) {
    var delivered = current === STEP_KEYS.length - 1;
    var warn = action !== "none";
    var n = STEP_KEYS.length;

    var cps = shipment.checkpoints || [];
    var expected = shipment.expected;
    var dates = [];
    dates[0] = formatShortDate(order && order.placedAt);
    if (delivered) dates[n - 1] = formatShortDate(cps.length > 0 ? cps[0].time : null);
    else if (expected && !expected.display) {
      dates[n - 1] = formatDateRange(expected.from, expected.to);
    }

    var journey = el("div", "ni-journey");
    if (warn) journey.className += " ni-journey-warn";
    if (delivered) journey.className += " ni-journey-delivered";
    journey.setAttribute("role", "img");
    journey.setAttribute(
      "aria-label",
      T.steps
        .map(function (label, idx) {
          return label + (idx === current ? " (" + T.now + ")" : "");
        })
        .join(", "),
    );

    // The track runs between the first and last circle centers.
    var inset = 50 / n + "%";
    var track = el("div", "ni-journey-track");
    track.style.left = inset;
    track.style.right = inset;
    var fill = el("div", "ni-journey-fill");
    fill.style.width = (current / (n - 1)) * 100 + "%";
    track.appendChild(fill);
    journey.appendChild(track);

    var nodes = el("div", "ni-journey-nodes");
    for (var i = 0; i < n; i++) {
      var state = i < current ? "done" : i === current ? "current" : "next";
      var node = el("div", "ni-journey-node ni-" + state);

      var iconName;
      if (state === "done" || (state === "current" && delivered)) iconName = "check";
      else if (state === "current" && warn) iconName = "warn";
      else iconName = STAGE_ICONS[i];
      var circle = el("div", "ni-journey-circle");
      circle.appendChild(iconEl(iconName, "ni-journey-glyph"));
      node.appendChild(circle);

      node.appendChild(el("span", "ni-journey-label", T.steps[i]));
      var statusText = dates[i] || (state === "current" ? T.now : "");
      if (statusText) node.appendChild(el("span", "ni-journey-status", statusText));
      nodes.appendChild(node);
    }
    journey.appendChild(nodes);
    return journey;
  }

  function aggregateItems(items) {
    var byTitle = {};
    var order = [];
    for (var i = 0; i < (items || []).length; i++) {
      var item = items[i];
      if (!item || !item.title) continue;
      if (!Object.prototype.hasOwnProperty.call(byTitle, item.title)) {
        byTitle[item.title] = { quantity: 0, imageUrl: null };
        order.push(item.title);
      }
      byTitle[item.title].quantity += item.quantity || 1;
      if (!byTitle[item.title].imageUrl && item.imageUrl) {
        byTitle[item.title].imageUrl = item.imageUrl;
      }
    }
    return order.map(function (title) {
      return {
        title: title,
        quantity: byTitle[title].quantity,
        imageUrl: byTitle[title].imageUrl,
      };
    });
  }

  function renderShipment(shipment, index, total, order) {
    var current = stepOf(shipment);
    var key = STEP_KEYS[current];
    var action = actionOf(shipment);
    var delivered = key === "delivered";
    var card = el("section", "ni-card ni-shipment");

    if (total > 1) {
      card.appendChild(
        el("p", "ni-eyebrow ni-shipment-count", T.shipmentOf(index + 1, total)),
      );
    }

    // 1. Where it is, in one line, and what that means.
    var statusBlock = el("div", "ni-status");
    statusBlock.appendChild(
      el(
        "h3",
        "ni-status-headline" +
          (delivered ? " ni-status-delivered" : "") +
          (action !== "none" ? " ni-status-warn" : ""),
        action !== "none" ? T.actionHeadline[action] : T.stepTitle[key],
      ),
    );
    statusBlock.appendChild(
      el("p", "ni-status-sub", action !== "none" ? T.actionSub[action] : T.stepText[key]),
    );
    card.appendChild(statusBlock);

    // 2. The step bar.
    card.appendChild(renderJourney(shipment, order, current, action));

    // 3. When it arrives.
    // (Hidden once it is out for delivery: the window no longer helps.)
    if (current < STEP_KEYS.indexOf("out_for_delivery") && shipment.expected) {
      var expectedText = shipment.expected.display
        ? shipment.expected.display
        : formatDateRange(shipment.expected.from, shipment.expected.to);
      if (expectedText) {
        var expected = el("div", "ni-expected");
        expected.appendChild(el("p", "ni-eyebrow", T.expectedTitle));
        expected.appendChild(el("p", "ni-expected-value", expectedText));
        card.appendChild(expected);
      }
    }

    // 4. Do I need to do anything? Almost always: no, and we say so.
    if (action !== "none") {
      card.appendChild(
        renderCallout("ni-notice", T.actionTitle, T.actionText[action], true),
      );
    } else if (shipment.late && !delivered) {
      card.appendChild(renderCallout("ni-notice", T.lateTitle, T.lateBody, true));
    } else {
      card.appendChild(
        renderCallout(
          "ni-ok",
          delivered ? T.deliveredHelpTitle : T.noActionTitle,
          delivered ? T.deliveredHelp : T.noActionBody,
          false,
        ),
      );
    }

    var place = currentLocation(shipment);
    if (place && !delivered) {
      var loc = el("div", "ni-journey-loc-wrap");
      var chip = el("span", "ni-journey-loc");
      chip.appendChild(iconEl("pin", "ni-journey-loc-pin"));
      chip.appendChild(el("span", null, place));
      loc.appendChild(chip);
      card.appendChild(loc);
    }

    // Carrier name and tracking number are intentionally not shown to
    // the customer (operator decision): the linehaul carrier is generic
    // and the raw number is noise.

    var items = aggregateItems(shipment.items);
    if (items.length > 0) {
      var itemsWrap = el("div", "ni-items");
      itemsWrap.appendChild(el("p", "ni-eyebrow", T.items));
      var ul = el("ul", "ni-items-list");
      for (var i = 0; i < items.length; i++) {
        var li = el("li");
        if (items[i].imageUrl) {
          var img = document.createElement("img");
          img.className = "ni-item-thumb";
          img.src = items[i].imageUrl;
          img.alt = items[i].title;
          img.loading = "lazy";
          li.appendChild(img);
        } else {
          li.appendChild(iconEl("shipped", "ni-item-thumb ni-item-thumb-empty"));
        }
        var itemBody = el("div", "ni-item-body");
        itemBody.appendChild(el("p", "ni-item-title", items[i].title));
        itemBody.appendChild(el("p", "ni-item-qty", "\u00d7 " + items[i].quantity));
        li.appendChild(itemBody);
        ul.appendChild(li);
      }
      itemsWrap.appendChild(ul);
      card.appendChild(itemsWrap);
    }

    // The raw carrier scans stay available, folded away: they are what
    // confused customers, the step above already says what they mean.
    var events = shipment.checkpoints || [];
    if (events.length > 0 && !(shipment.preHandoff && events.length === 1)) {
      card.appendChild(renderTimeline(events, shipment, current, action));
    }

    return card;
  }

  function renderCallout(className, title, body, showContact) {
    var box = el("div", className);
    box.appendChild(el("p", className + "-title", title));
    box.appendChild(el("p", className + "-body", body));
    if (showContact && CONTACT) {
      var link = el("a", className + "-link", T.contactCta);
      link.href = contactHref(T.mailSubject(currentOrderNumber || ""));
      box.appendChild(link);
    }
    return box;
  }

  function renderTimeline(events, shipment, current, action) {
    var delivered = STEP_KEYS[current] === "delivered";
    var warn = action !== "none";
    var block = el("details", "ni-updates");
    if (delivered) block.className += " ni-updates-delivered";
    if (warn) block.className += " ni-updates-warn";
    block.appendChild(el("summary", "ni-updates-toggle", T.historyToggle(events.length)));
    if (shipment.quietDays != null) {
      block.appendChild(el("p", "ni-quiet", T.quietNote(shipment.quietDays)));
    }

    var list = el("div", "ni-timeline");
    // Events arrive newest-first. Group consecutive events by calendar
    // day so each day gets one heading, then a rail of timed rows.
    var groups = [];
    var currentKey = null;
    for (var j = 0; j < events.length; j++) {
      var key = dayKey(events[j].time);
      if (key !== currentKey) {
        currentKey = key;
        groups.push({ header: formatDayHeader(events[j].time), items: [] });
      }
      groups[groups.length - 1].items.push({ ev: events[j], globalIndex: j });
    }

    for (var g = 0; g < groups.length; g++) {
      var group = groups[g];
      var dayEl = el("div", "ni-day");
      if (group.header) {
        dayEl.appendChild(el("p", "ni-day-header", group.header));
      }
      var rows = el("ol", "ni-day-rows");
      for (var r = 0; r < group.items.length; r++) {
        var ev = group.items[r].ev;
        var isLatest = group.items[r].globalIndex === 0;
        var row = el("li", "ni-event" + (isLatest ? " ni-event-latest" : ""));

        var time = el("div", "ni-event-time", formatTimeOnly(ev.time) || "");
        row.appendChild(time);

        var iconName = isLatest
          ? warn
            ? "warn"
            : STAGE_ICONS[current]
          : "check";
        row.appendChild(
          iconEl(
            iconName,
            "ni-event-dot" + (isLatest ? " ni-event-dot-latest" : ""),
          ),
        );

        var body = el("div", "ni-event-body");
        body.appendChild(
          el("p", "ni-event-desc", normalizeDescription(ev.description) || ""),
        );
        if (ev.location) {
          var place = el("p", "ni-event-loc");
          place.appendChild(iconEl("pin", "ni-event-loc-pin"));
          place.appendChild(
            el("span", null, titleCasePlace(ev.location)),
          );
          body.appendChild(place);
        }
        row.appendChild(body);
        rows.appendChild(row);
      }
      dayEl.appendChild(rows);
      list.appendChild(dayEl);
    }

    block.appendChild(list);
    return block;
  }

  function contactHref(subject) {
    return (
      "mailto:" + CONTACT + "?subject=" + encodeURIComponent(subject)
    );
  }

  function renderContact(orderNumber) {
    if (!CONTACT) return null;
    var block = el("div", "ni-contact");
    block.appendChild(el("p", "ni-contact-title", T.contactTitle));
    var btn = el("a", "ni-contact-btn");
    btn.href = contactHref(
      orderNumber ? T.mailSubject(orderNumber) : T.mailSubjectGeneric,
    );
    btn.appendChild(iconEl("mail", "ni-contact-icon"));
    btn.appendChild(el("span", null, T.contactCta));
    block.appendChild(btn);
    return block;
  }

  // ------------------------------------------------------------------
  // Lookup
  // ------------------------------------------------------------------

  function lookup(payload, typedOrder) {
    renderLoading();
    var req;
    if (payload.k) {
      var qs =
        "?store=" +
        encodeURIComponent(payload.store) +
        "&order=" +
        encodeURIComponent(payload.order) +
        "&k=" +
        encodeURIComponent(payload.k);
      req = fetch(API + qs, { method: "GET" });
    } else {
      req = fetch(API, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
    }
    req
      .then(function (res) {
        if (res.status === 429) throw new Error("rate");
        return res.json();
      })
      .then(function (data) {
        if (data && data.found) renderResult(data);
        else renderForm(typedOrder || "", T.notFound);
      })
      .catch(function () {
        renderForm(typedOrder || "", T.genericError);
      });
  }

  var params = new URLSearchParams(window.location.search);
  var qOrder = params.get("order");
  var qKey = params.get("k");
  if (qOrder && qKey) {
    lookup({ store: STORE, order: qOrder, k: qKey }, qOrder);
  } else {
    renderForm(qOrder || "", null);
  }
})();
